/**
 * space_flight_combat.ts — the sector's AIMING, FIRE GROUPS and EW glue (space campaign I, 2026-09-30).
 *
 * SpaceModule hands this controller a narrow HOST (its fire functions, its capacitor, the ship's pose, the target, the
 * remote pilots it already renders, the war targets it already lists) and calls it from four places only: the lead pip
 * block, the two primary-trigger call sites + the secondary trigger + the group swap, the countermeasure, and once per
 * frame for the HUD. Everything that decides lives in the pure files beside it:
 *   space_lead.ts        the quadratic intercept, its confidence, the velocity track
 *   space_fire_groups.ts two groups over the fitted mounts, the fit revision, the Loadout section
 *   space_ew_client.ts   the EW rows this pilot sees, the jammer's aim, the requests, the HUD's words
 *   space_combat_hud.ts  the DOM layer (lead note, jammer contact, EW strip, group chip)
 * THE AERO LAW is untouched: nothing here steers, throttles or banks — it only aims and commits resources.
 */
import * as THREE from 'three/webgpu';
import { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { keyHint } from './keybinds.js';
import { solveLead, leadLabel, LeadTracker, BOLT_SPEED_MPS, BOLT_LIFE_S, JAM_DELAY_MS, type LeadSolution, type V3 } from './space_lead.js';
import {
  loadGroups, saveGroups, validateGroups, triggerMount, cycleGroup as cycleState, groupChip, cycleToast, fitRevision,
  defaultState, mountFitted, primaryAction, groupsSaveSeq, hasSavedGroups, MOUNT_LABEL, type FireGroupsState, type Slot,
} from './space_fire_groups.js';
import { ew, pickJamTarget, EW_COSTS } from './space_ew_client.js';
import { CombatHud, type ScreenPt } from './space_combat_hud.js';
import type { EwSolid } from '@shared/space_ew.js';

export interface FlightTarget { key: string; pos: THREE.Vector3; vel: THREE.Vector3; hitR: number; seatAt: number | null }
export interface FlightHost {
  fireBolts(): void;
  fireMissile(): void;
  cap(): number;
  spendCap(n: number): void;
  fit(): { missiles: number; counters: number };
  /** In the cockpit of the pilot's own hull, flying (not docked, not walking, not in a crew seat). */
  flying(): boolean;
  crewSeated(): boolean;
  toast(text: string): void;
  /** The ship: absolute position, offset-space position (the render frame), velocity, nose direction. */
  ship(): { abs: V3; local: THREE.Vector3; vel: THREE.Vector3; fwd: V3 };
  /** Offset → absolute: abs = local + off. */
  off(): THREE.Vector3;
  warTargets(): ReadonlySet<string>;
  /** The remote pilots rendered in this system: the drawn hull (offset space) and the server row's position (offset). */
  remotes(): Iterable<[string, { obj: THREE.Object3D; target: THREE.Vector3 }]>;
  target(): FlightTarget | null;
  surveying(): boolean;
  /** Solid colliders the client knows near a point (absolute). */
  solidsNear(abs: V3, radius: number): EwSolid[];
  /** The system this client renders. */
  systemId(): string;
  /** Offset space → the camera's WORLD space, in place (THE SCENE SHIFT: the ground rebase moves the scene). */
  toWorld(v: THREE.Vector3): THREE.Vector3;
}

const nameCache = new Map<string, string>();
function pilotName(hex: string): string {
  const hit = nameCache.get(hex);
  if (hit) return hit;
  let n = '';
  try { n = atlas.conn?.db.account.identity.find(new Identity(hex))?.username ?? ''; } catch { /* mid-churn */ }
  const out = n || `pilot ${hex.slice(0, 6)}`;
  if (n) nameCache.set(hex, out);
  return out;
}

export class FlightCombat {
  readonly tracker = new LeadTracker();
  readonly hud = new CombatHud();
  groups: FireGroupsState = defaultState();
  private groupsShip = '';
  private seenSeq = -1;
  private primaryWas = false;
  private slowAcc = 0;
  private syncAt = 0;
  private readonly tmp = new THREE.Vector3();
  private readonly tmp2 = new THREE.Vector3();
  private readonly jamPos = new THREE.Vector3();
  private readonly jamWorld = new THREE.Vector3();
  private watchAt = 0;
  private readonly remoteTrack = new Map<string, { pos: THREE.Vector3; vel: THREE.Vector3; changedMs: number }>();
  /** Probe telemetry. */
  readonly telemetry = {
    lead: null as null | { kind: string; t: number; confidence: string; missM: number; label: string; reasons: string[]; jammed: boolean; r: V3; v: V3; hitR: number; pip: ScreenPt | null; key: string; shipLocal: V3 },
    fired: { bolts: 0, missile: 0, jammer: 0, none: 0 },
    groupNotes: [] as string[],
    jamRefusals: [] as string[],
    jamView: null as null | { local: number[]; pos: number[]; ship: number[] },
    syncs: 0, syncShip: '', syncError: '',
  };

  private leadSeen = false;

  constructor(private readonly h: FlightHost) {
    // Probe lever (read-only telemetry + a reload after a probe rewrites the stored groups).
    if (typeof window !== 'undefined') (window as unknown as Record<string, unknown>).__flight = { state: () => this.state(), reload: () => this.reloadGroups() };
  }

  // ── Fire groups ────────────────────────────────────────────────────────────────────────────────────────────────
  /** The active hull's groups, re-validated against its fit revision (≤ 1 Hz). */
  syncGroups(nowMs: number, force = false): void {
    if (!force && nowMs - this.syncAt < 1000) return;
    this.syncAt = nowMs;
    const c = atlas.conn, me = c?.identity;
    if (!c || !me) return;
    let shipId = '';
    const comps: Array<{ compId: bigint; slot: string; quality: number }> = [];
    this.telemetry.syncs++;
    try {
      for (const s of c.db.playerShip.iter()) if (s.owner.isEqual(me) && s.active) { shipId = String(s.shipId); break; }
      if (shipId) for (const k of c.db.shipComponent.iter()) if (String(k.shipId) === shipId) comps.push({ compId: k.compId, slot: k.slot, quality: k.quality });
    } catch (e) { this.telemetry.syncError = String((e as Error)?.message ?? e); return; /* mid-churn */ }
    this.telemetry.syncShip = shipId;
    if (!shipId && this.groupsShip && this.groupsShip !== 'stock') { this.telemetry.groupNotes.push('(no active hull row this instant — kept the hull)'); return; }
    // No active row this instant (a cache churn) keeps the hull we had — a flip to the 'stock' key read group 1 back.
    const key = shipId || this.groupsShip || 'stock';
    const rev = fitRevision(comps);
    // Re-read storage only for another hull or when the dock's Loadout saved (never over this controller's own swap —
    // a 1 Hz blind reload raced the swap key and put group 1 back).
    if (key !== this.groupsShip || groupsSaveSeq() !== this.seenSeq || force) {
      // The hull row arriving after a swap made under the provisional 'stock' key: carry the pilot's choice over
      // (a hull with no stored grouping takes the one in hand) instead of reading group 1 back.
      const carry = key !== this.groupsShip && (this.groupsShip === 'stock' || this.groupsShip === '') && !hasSavedGroups(key);
      this.groups = carry ? { ...this.groups } : loadGroups(key);
      this.groupsShip = key;
    }
    this.seenSeq = groupsSaveSeq();
    if (this.groups.fitRev === rev && !force) return;
    const v = validateGroups(this.groups, this.h.fit(), rev);
    this.groups = v.state;
    saveGroups(key, v.state);
    this.seenSeq = groupsSaveSeq();
    for (const n of v.notes) { this.h.toast(n); this.telemetry.groupNotes.push(n); }
  }
  /** The probe / Loadout changed the stored groups: read them back. */
  reloadGroups(): void { this.groupsShip = ''; this.syncAt = 0; this.syncGroups(performance.now(), true); }

  private fireMount(m: Slot): void {
    this.telemetry.fired[m]++;
    if (m === 'bolts') this.h.fireBolts();
    else if (m === 'missile') this.h.fireMissile();
    else if (m === 'jammer') this.fireJammer();
    else this.h.toast(`Fire group ${this.groups.active + 1} has nothing on that trigger — set it in the dock's Loadout (${keyHint('spaceDock')} at a station) or swap groups (${keyHint('spaceFireGroup')})`);
  }
  /** The primary trigger, every fixed tick: bolts while held (their cooldown paces them); a missile or the jammer once
   *  per PRESS (the rising edge) — a held trigger never streams the rack. */
  primaryHeld(held: boolean): void {
    const m = primaryAction(this.groups, held, this.primaryWas);
    this.primaryWas = held;
    if (m === 'bolts') this.h.fireBolts(); // not counted per tick in the telemetry — the volley count is fire()'s
    else if (m) this.fireMount(m);
  }
  /** A left click: one volley / one missile / one jam (the click was always one volley). */
  primaryClick(): void { this.fireMount(triggerMount(this.groups, 'primary')); }
  secondary(): void { this.fireMount(triggerMount(this.groups, 'secondary')); }
  cycleGroup(): void {
    this.groups = cycleState(this.groups);
    saveGroups(this.groupsShip || 'stock', this.groups);
    this.seenSeq = groupsSaveSeq();
    this.h.toast(cycleToast(this.groups, this.h.fit()));
  }

  // ── EW ─────────────────────────────────────────────────────────────────────────────────────────────────────────
  /** The jammer: the warring pilot nearest the nose (≤ 600 m, 25°, in sight of the colliders this client knows). */
  fireJammer(): void {
    ew.ensure();
    const nowMs = Date.now();
    const why = (t: string): void => { this.telemetry.jamRefusals.push(t); if (this.telemetry.jamRefusals.length > 8) this.telemetry.jamRefusals.shift(); this.h.toast(t); };
    if (this.h.crewSeated()) { why('The turret seat fires the turret — the jammer is your own hull\'s'); return; }
    if (!mountFitted('jammer', this.h.fit())) { why('No jammer fitted — the Countermeasures pod carries the EW suite (fit one in the Loadout)'); return; }
    const cd = ew.jamReadyInMs(nowMs);
    if (cd > 0) { why(`The jammer is cycling — ${Math.ceil(cd / 1000)} s`); return; }
    if (this.h.cap() < EW_COSTS.jam) { why(`Capacitor too low for the jammer (${EW_COSTS.jam})`); return; }
    const s = this.h.ship(), wars = this.h.warTargets();
    const cands: Array<{ hex: string; pos: V3 }> = [];
    for (const hex of wars) { const p = this.serverPos(hex); if (p) cands.push({ hex, pos: p }); }
    const pick = pickJamTarget(s.abs, s.fwd, cands, this.h.solidsNear(s.abs, 800));
    if (pick.hex === null) { why(pick.why); return; }
    const hex = pick.hex;
    ew.jam(hex, (ok, msg) => {
      if (!ok) { why(`Jammer: ${msg}`); return; }
      this.h.spendCap(EW_COSTS.jam); // the capacitor pays on the server's acceptance
      this.h.toast(`≋ Jamming ${pilotName(hex)} — six seconds; their survey work slows to two-thirds (breaks past 750 m)`);
    });
  }
  /** The countermeasure while jammed: the sensor burst. True when a jam was there to clear (a burst was sent). */
  countermeasure(): boolean {
    ew.ensure();
    const nowMs = Date.now();
    if (!ew.onMe(nowMs).length) return false;
    const cd = ew.burstReadyInMs(nowMs);
    if (cd > 0) { this.h.toast(`The sensor burst is recharging — ${Math.ceil(cd / 1000)} s (fly past 750 m, or shoot the ◇ jammer)`); return true; }
    if (this.h.cap() < EW_COSTS.burst) { this.h.toast(`Capacitor too low for a sensor burst (${EW_COSTS.burst})`); return true; }
    ew.burst((ok, msg) => {
      if (!ok) { this.h.toast(`Sensor burst: ${msg}`); return; }
      this.h.spendCap(EW_COSTS.burst);
      this.h.toast('◈ Sensor burst — the interference is cleared; your sensors resist it for 4 s');
    });
    return true;
  }

  /** A pilot's position as the RULES read it (absolute): the AOI remote's server row, else the watched row — only in
   *  this system and in flight (THE AOI GAP: past ~190 m a pilot is no remote). */
  private serverPos(hex: string): V3 | null {
    const off = this.h.off();
    for (const [k, rs] of this.h.remotes()) if (k === hex) return { x: rs.target.x + off.x, y: rs.target.y + off.y, z: rs.target.z + off.z };
    const r = ew.rowOf(hex);
    if (!r || r.systemId !== this.h.systemId() || r.mode !== 'flight') return null;
    return { x: r.x, y: r.y, z: r.z };
  }
  /** Where to DRAW a pilot (offset space): the rendered hull when the AOI holds it, else its server row. */
  private drawnPos(hex: string, out: THREE.Vector3): THREE.Vector3 | null {
    for (const [k, rs] of this.h.remotes()) if (k === hex) return out.copy(rs.obj.position);
    const p = this.serverPos(hex);
    if (!p) return null;
    const off = this.h.off();
    return out.set(p.x - off.x, p.y - off.y, p.z - off.z);
  }

  // ── The lead (SpaceModule's pip block) ─────────────────────────────────────────────────────────────────────────
  /** Solve the true lead on the current target; returns where the pip goes (null when there is no hittable solution)
   *  and writes the note beside the bracket. `bracket` is the target's screen point (null when off screen). */
  lead(cam: THREE.Camera, w: number, h: number, bracket: ScreenPt | null): ScreenPt | null {
    const tgt = this.h.target();
    if (!tgt) { this.hud.setLead(null); this.telemetry.lead = null; return null; }
    this.leadSeen = true;
    const nowP = performance.now();
    this.tracker.observe(tgt.key, nowP, tgt.vel);
    const jammed = ew.onMe(Date.now()).length > 0;
    const s = this.h.ship();
    const tv = jammed ? (this.tracker.delayed(tgt.key, nowP, JAM_DELAY_MS) ?? tgt.vel) : tgt.vel;
    const sol: LeadSolution = solveLead({
      r: { x: tgt.pos.x - s.local.x, y: tgt.pos.y - s.local.y, z: tgt.pos.z - s.local.z },
      v: { x: tv.x - s.vel.x, y: tv.y - s.vel.y, z: tv.z - s.vel.z }, // the bolt inherits the ship's velocity
      s: BOLT_SPEED_MPS, life: BOLT_LIFE_S, accel: this.tracker.accel(tgt.key), hitRadius: tgt.hitR,
      sampleAgeS: tgt.seatAt !== null ? (nowP - tgt.seatAt) / 1000 : 0, jammed,
    });
    let pip: ScreenPt | null = null;
    if (sol.kind !== 'none') {
      const p = this.h.toWorld(this.tmp.set(s.local.x + sol.aim.x, s.local.y + sol.aim.y, s.local.z + sol.aim.z));
      const local = this.tmp2.copy(p).applyMatrix4(cam.matrixWorldInverse);
      if (local.z < 0) {
        const ndc = p.project(cam);
        if (Math.abs(ndc.x) <= 1.05 && Math.abs(ndc.y) <= 1.05) pip = { x: (ndc.x * 0.5 + 0.5) * w, y: (-ndc.y * 0.5 + 0.5) * h };
      }
    }
    const label = leadLabel(sol);
    this.hud.mount();
    this.hud.setLead({ kind: sol.kind, confidence: sol.confidence, label, pip, bracket });
    this.telemetry.lead = {
      kind: sol.kind, t: sol.t, confidence: sol.confidence, missM: sol.missM, label, reasons: sol.reasons, jammed,
      r: { x: tgt.pos.x - s.local.x, y: tgt.pos.y - s.local.y, z: tgt.pos.z - s.local.z }, v: { x: tv.x - s.vel.x, y: tv.y - s.vel.y, z: tv.z - s.vel.z },
      hitR: tgt.hitR, pip, key: tgt.key, shipLocal: { x: s.local.x, y: s.local.y, z: s.local.z },
    };
    return sol.kind === 'lead' ? pip : null;
  }

  // ── Once per frame: the jammer contact; ~5 Hz: the EW strip, the group chip, the groups' fit check ────────────────
  frame(cam: THREE.Camera, w: number, h: number, dt: number): void {
    this.hud.mount();
    const nowMs = Date.now(), nowP = performance.now();
    ew.ensure();
    this.trackRemotes(nowP);
    const flying = this.h.flying();
    if (!this.leadSeen || !flying) this.hud.setLead(null); // the target is behind me, gone, or I left the cockpit
    this.leadSeen = false;
    // The jammer contact (only while I am jammed): a ◇ bracket + its own lead pip, or a bearing arrow.
    const on = flying ? ew.onMe(nowMs) : [];
    if (on.length) {
      const hex = on[0].source.toHexString();
      const rsPos = this.drawnPos(hex, this.jamPos);
      if (rsPos) {
        cam.updateWorldMatrix(true, false);
        (cam as THREE.PerspectiveCamera).matrixWorldInverse.copy(cam.matrixWorld).invert();
        const wPos = this.h.toWorld(this.jamWorld.copy(rsPos));
        const local = this.tmp2.copy(wPos).applyMatrix4(cam.matrixWorldInverse);
        const ang = Math.atan2(local.x, local.y);
        this.telemetry.jamView = { local: [local.x, local.y, local.z].map((n) => Math.round(n)), pos: [rsPos.x, rsPos.y, rsPos.z].map((n) => Math.round(n)), ship: [this.h.ship().local.x, this.h.ship().local.y, this.h.ship().local.z].map((n) => Math.round(n)) };
        const s = this.h.ship();
        // The distance the RULE reads (the server row — it breaks past 750 m), not the lerped drawing.
        const sp = this.serverPos(hex);
        const dist = sp ? Math.hypot(sp.x - s.abs.x, sp.y - s.abs.y, sp.z - s.abs.z) : rsPos.distanceTo(s.local);
        const label = `◇ JAMMER ${pilotName(hex)} · ${Math.round(dist)} m`;
        if (local.z < 0) {
          const ndc = this.tmp.copy(wPos).project(cam);
          if (Math.abs(ndc.x) <= 1.05 && Math.abs(ndc.y) <= 1.05) {
            const tr = this.remoteTrack.get(hex);
            const vel = tr?.vel ?? new THREE.Vector3();
            const sol = solveLead({
              r: { x: rsPos.x - s.local.x, y: rsPos.y - s.local.y, z: rsPos.z - s.local.z },
              v: { x: vel.x - s.vel.x, y: vel.y - s.vel.y, z: vel.z - s.vel.z },
              hitRadius: 14, sampleAgeS: tr ? (nowP - tr.changedMs) / 1000 : 1, jammed: true,
            });
            let lead: ScreenPt | null = null;
            if (sol.kind === 'lead') {
              const lp = this.h.toWorld(this.tmp.set(s.local.x + sol.aim.x, s.local.y + sol.aim.y, s.local.z + sol.aim.z)).project(cam);
              if (Math.abs(lp.x) <= 1.05 && Math.abs(lp.y) <= 1.05) lead = { x: (lp.x * 0.5 + 0.5) * w, y: (-lp.y * 0.5 + 0.5) * h };
            }
            this.hud.setJammer({ bracket: { x: (ndc.x * 0.5 + 0.5) * w, y: (-ndc.y * 0.5 + 0.5) * h }, lead, arrowAng: null, label });
          } else this.hud.setJammer({ bracket: null, lead: null, arrowAng: ang, label });
        } else this.hud.setJammer({ bracket: null, lead: null, arrowAng: ang, label });
      } else this.hud.setJammer(null);
    } else this.hud.setJammer(null);
    // ~5 Hz words.
    this.slowAcc += dt;
    if (this.slowAcc < 0.2) return;
    this.slowAcc = 0;
    this.syncGroups(nowP);
    if (!flying) { this.hud.setChip(''); this.hud.setStrip('none', []); return; }
    this.hud.setChip(groupChip(this.groups, this.h.fit()));
    const s = this.h.ship();
    // THE AOI GAP: watch the rows of the EW parties and the warring pilots in reach of a jammer (≤ 32), 1 Hz.
    if (nowP - this.watchAt > 1000) {
      this.watchAt = nowP;
      const wars = [...this.h.warTargets()].sort();
      ew.watch([...ew.parties(nowMs), ...(this.h.fit().counters > 0 || ew.onMe(nowMs).length ? wars : [])]);
    }
    const v = ew.hud({
      nowMs, nameOf: pilotName, surveying: this.h.surveying(),
      sepTo: (hex) => { const p = this.serverPos(hex); return p ? Math.hypot(p.x - s.abs.x, p.y - s.abs.y, p.z - s.abs.z) : null; },
    });
    this.hud.setStrip(v.tone, v.lines, v.compact);
  }

  /** Velocities of the remote pilots from their SERVER rows (the drawn hull is lerped): Δposition / Δtime between
   *  row changes, and when the last change arrived (the lead's sample age). */
  private trackRemotes(nowP: number): void {
    // The jammer outside the AOI: its velocity from its watched row (offset space, same arithmetic).
    for (const hex of ew.parties(Date.now())) {
      const p = this.serverPos(hex);
      if (!p) continue;
      const off = this.h.off();
      const pos = this.tmp.set(p.x - off.x, p.y - off.y, p.z - off.z);
      const tr = this.remoteTrack.get(hex);
      if (!tr) { this.remoteTrack.set(hex, { pos: pos.clone(), vel: new THREE.Vector3(), changedMs: nowP }); continue; }
      if (tr.pos.distanceToSquared(pos) > 1e-4) {
        const dt = Math.max(0.05, (nowP - tr.changedMs) / 1000);
        tr.vel.copy(pos).sub(tr.pos).divideScalar(dt);
        tr.pos.copy(pos);
        tr.changedMs = nowP;
      }
    }
    for (const [hex, rs] of this.h.remotes()) {
      const tr = this.remoteTrack.get(hex);
      if (!tr) { this.remoteTrack.set(hex, { pos: rs.target.clone(), vel: new THREE.Vector3(), changedMs: nowP }); continue; }
      if (tr.pos.distanceToSquared(rs.target) > 1e-4) {
        const dt = Math.max(0.05, (nowP - tr.changedMs) / 1000);
        tr.vel.copy(rs.target).sub(tr.pos).divideScalar(dt);
        tr.pos.copy(rs.target);
        tr.changedMs = nowP;
      }
    }
    if (this.remoteTrack.size > 64) this.remoteTrack.clear();
    this.tracker.prune(nowP);
  }

  unmount(): void { this.hud.unmount(); this.primaryWas = false; ew.watch([]); /* the watched rows leave with the sector */ }

  /** Probe lever payload. */
  state(): object {
    return {
      groups: this.groups, groupsShip: this.groupsShip, fit: this.h.fit(), lead: this.telemetry.lead,
      sync: { n: this.telemetry.syncs, ship: this.telemetry.syncShip, error: this.telemetry.syncError }, fired: { ...this.telemetry.fired },
      groupNotes: [...this.telemetry.groupNotes], jamRefusals: [...this.telemetry.jamRefusals], hud: { ...this.hud.shown }, jamView: this.telemetry.jamView,
      ew: { stats: { ...ew.stats }, onMe: ew.onMe(Date.now()).map((e) => ({ id: String(e.effectId), source: e.source.toHexString(), expiresMs: Number(e.expiresAt.microsSinceUnixEpoch / 1000n) })), jamReadyInMs: ew.jamReadyInMs(Date.now()), burstReadyInMs: ew.burstReadyInMs(Date.now()) },
      mountLabels: MOUNT_LABEL,
    };
  }
}
